/**
 * A quick live's own page: the call itself (on air, just ended, or with its replay), who was in it, and the one way
 * to follow up. It stands where a quick live's room page used to, and has no registration page, tabs or invites.
 */
import { type ReactNode, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { SYSTEM_TAG_NAMES } from "../../../shared/tags";
import type { RoomPerson, WebinarDetail, WebinarLive } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { studioPath } from "../../board/LiveStrip";
import { GoLiveSheet } from "../../components/GoLiveSheet";
import { Empty } from "../../components/season/Empty";
import { type Layout, useLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { PushedNav } from "../../components/season/PushedNav";
import { Button, IconButton } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { Face, FaceStack } from "../../components/ui/Face";
import { Field } from "../../components/ui/Field";
import { Icon } from "../../components/ui/Icon";
import { Pill } from "../../components/ui/Pill";
import { Sheet } from "../../components/ui/Sheet";
import { normalizeTag, TagPicker } from "../../components/ui/TagPicker";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import "../../styles/rooms.css";
import "../../styles/room-live.css";
import { teamTestsLine } from "./liveWords";
import { callWhen, pageReplay, ranWords, replayMailLine, stayedWords } from "./quickWords";
import { LivePicture } from "./LivePicture";
import { ReplaySheet } from "./ReplaySheet";
import { copyRoomLink, RoomMenu } from "./RoomActions";
import { useQuickLive } from "./useQuickLive";

const n = (v: number) => v.toLocaleString("en-US");
const photos = (people: readonly RoomPerson[]) => people.filter((p) => p.avatar_url).slice(0, 4);
const faceList = (people: readonly RoomPerson[]) => photos(people).map((p) => ({ name: p.name, photo: p.avatar_url }));

/** One press of a send, kept the same if it is pressed again so it never goes twice. */
const newPress = () => `msg${crypto.randomUUID().replaceAll("-", "")}`.slice(0, 40);

function Header({ detail, live, people, reload }: { detail: WebinarDetail; live: WebinarLive; people: readonly RoomPerson[]; reload: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [goingLive, setGoingLive] = useState(false);
  const onAir = live.state === "live";
  const ran = ranWords(live, Date.now());
  const room = { ...detail, registered: detail.registered_here };
  return (
    <>
      <div className="whead cp-head">
        <div className="thumb cp-thumb">
          <LivePicture poster={detail.poster_url} title={detail.title} faces={people} />
        </div>
        <div className="wht" style={{ minWidth: 0 }}>
          <div className="row g8 wrap">
            {onAir ? (
              <Pill tone="livep" dot>
                Live now
              </Pill>
            ) : (
              <Pill tone="line">Quick live</Pill>
            )}
            <span className="small muted">{[callWhen(live.startsAt, live.timezone, Date.now()), ran && !onAir ? `ran ${ran}` : ran].filter(Boolean).join(" · ")}</span>
          </div>
          <h1 className="mt6">{detail.title}</h1>
          <div className="linkchip mt8">
            <Icon name="globe" />
            <span>{detail.register_url.replace(/^https?:\/\//, "")}</span>
            <IconButton icon="copy" label="Copy link" small onClick={() => void copyRoomLink(detail.register_url, toast)} />
          </div>
        </div>
        <div className="row g8 wha">
          {onAir ? null : (
            <button type="button" className="btn live" onClick={() => setGoingLive(true)}>
              <span className="dot" />
              Go live again
            </button>
          )}
          <RoomMenu room={room} bordered onChanged={reload} onDeleted={() => navigate("/rooms", { replace: true })} />
        </div>
      </div>
      {goingLive ? <GoLiveSheet hostFirst={user?.first_name ?? ""} onClose={() => setGoingLive(false)} onMade={reload} /> : null}
    </>
  );
}

function ReplayCard({ detail, live, people }: { detail: WebinarDetail; live: WebinarLive; people: readonly RoomPerson[] }) {
  const [playing, setPlaying] = useState(false);
  const replay = pageReplay(live);
  return (
    <section className="card cp-rep">
      <div className="cp-rep-pic">
        <LivePicture poster={detail.poster_url} title={detail.title} faces={people} />
        {replay === "ready" ? (
          <span className="cp-play">
            <Icon name="play" />
          </span>
        ) : replay === "building" ? (
          <span className="cp-wait">
            <b>The replay will be ready in a few minutes</b>
            <i className="cp-shim" />
          </span>
        ) : null}
      </div>
      <div className="cp-rep-t">
        <div className="eyebrow">The replay</div>
        {replay === "ready" ? (
          <>
            <p>Only the people who came can watch it.</p>
            <div className="cp-rep-b">
              <Button icon="play" onClick={() => setPlaying(true)}>
                Watch
              </Button>
            </div>
          </>
        ) : replay === "building" ? (
          <p>It is being made from the recording. The people who came see it on their page as soon as it is ready.</p>
        ) : (
          <p>This call was not recorded, so there is no replay.</p>
        )}
      </div>
      {playing ? <ReplaySheet webinarId={detail.id} sessionId={live.sessionId} title={detail.title} onClose={() => setPlaying(false)} /> : null}
    </section>
  );
}

function TagSheet({ people, onClose }: { people: readonly RoomPerson[]; onClose: () => void }) {
  const toast = useToast();
  const [tag, setTag] = useState("");
  const [own, setOwn] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const groups = [
    { label: "Flashroom tags", tags: SYSTEM_TAG_NAMES },
    { label: "Your tags", tags: own },
  ];
  const name = normalizeTag(tag);
  const go = () => {
    if (!name || busy) return;
    setBusy(true);
    setError("");
    Promise.all(people.map((p) => api.people.tag(p.registrantId, { add: [name] })))
      .then(() => {
        toast({ text: `Tagged ${n(people.length)} ${people.length === 1 ? "person" : "people"}: ${name}`, icon: "tag" });
        onClose();
      })
      .catch(() => {
        setError("The tag didn't save for everyone. Try again.");
        setBusy(false);
      });
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title={`Tag the ${n(people.length)} who came`}
      sub="Your automations take it from there."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="tag" disabled={busy || !name} onClick={go}>
            {busy ? "Tagging…" : "Tag them"}
          </Button>
        </>
      }
    >
      <Field label="Tag" error={error || undefined}>
        <TagPicker value={tag} groups={groups} onChange={setTag} onAdd={(t) => setOwn((o) => (o.includes(t) ? o : [...o, t]))} label="The tag for the people who came" />
      </Field>
    </Sheet>
  );
}

function WhoCame({ live, people }: { live: WebinarLive; people: readonly RoomPerson[] | null }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [tagging, setTagging] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const onAir = live.state === "live";
  const list = people === null ? [] : onAir ? people.filter((p) => p.here) : people;
  const open = (p: RoomPerson) => {
    if (opening) return;
    setOpening(p.registrantId);
    api.talk
      .directFor(p.registrantId)
      .then((r) => navigate(`/conversations/direct/${r.contactId}`))
      .catch((err: unknown) => {
        toast({ text: err instanceof ApiError ? err.message : "We couldn't open that conversation. Try again.", icon: "info" });
        setOpening(null);
      });
  };
  const head = onAir ? "Here now" : "Who came";
  return (
    <section className="card cp-who">
      <div className="cp-who-h">
        <h2>{people === null ? head : `${head} · ${n(list.length)}`}</h2>
        {onAir || list.length === 0 ? null : (
          <button type="button" className="cp-tag" onClick={() => setTagging(true)}>
            <Icon name="tag" /> Tag them
          </button>
        )}
      </div>
      {people !== null && list.length === 0 ? <p className="cp-pr small muted">{onAir ? "No one is here yet." : "No one came to this one."}</p> : null}
      {list.map((p) => (
        <div key={p.registrantId} className="cp-pr">
          <Face name={p.name} photo={p.avatar_url} size={36} />
          <span className="cp-t">
            <b>{p.name}</b>
            {p.email ? <span>{p.email}</span> : null}
            <span>{stayedWords(p, onAir)}</span>
          </span>
          <Button size="s" icon="chat" disabled={opening !== null} onClick={() => open(p)}>
            Message
          </Button>
        </div>
      ))}
      {tagging ? <TagSheet people={list} onClose={() => setTagging(false)} /> : null}
    </section>
  );
}

function MessageSheet({ detail, people, onClose }: { detail: WebinarDetail; people: readonly RoomPerson[]; onClose: () => void }) {
  const toast = useToast();
  const [body, setBody] = useState("");
  const [press] = useState(newPress);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const go = () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    setError("");
    api.talk
      .sendPrivately({ to: { registrantIds: people.map((p) => p.registrantId) }, body: body.trim(), email: true, subject: detail.title, requestId: press })
      .then((r) => {
        toast(`Sent to ${n(r.sent)} ${r.sent === 1 ? "person" : "people"}`);
        onClose();
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "We couldn't send that. Try again.");
        setBusy(false);
      });
  };
  return (
    <Sheet
      open
      wide
      onClose={onClose}
      title={`Message the ${n(people.length)} who came`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="send" disabled={busy || !body.trim()} onClick={go}>
            {busy ? "Sending…" : `Send to ${n(people.length)}`}
          </Button>
        </>
      }
    >
      <div className="col" style={{ gap: 14 }}>
        <div className="row g12">
          <FaceStack people={faceList(people)} size={32} more={Math.max(0, people.length - photos(people).length)} />
          <div className="small muted">Each one gets it in their own thread with you, and by email.</div>
        </div>
        <Field label="Message" htmlFor="qmsg" error={error || undefined}>
          <textarea id="qmsg" className="inp" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
      </div>
    </Sheet>
  );
}

function FollowUp({ detail, live, people, mail }: { detail: WebinarDetail; live: WebinarLive; people: readonly RoomPerson[]; mail: { at: number; held: boolean } | null }) {
  const [messaging, setMessaging] = useState(false);
  const status = replayMailLine(pageReplay(live), mail, live.cameLive);
  return (
    <section className="card cp-follow">
      <div className="eyebrow">Follow up</div>
      {people.length > 0 ? (
        <Button variant="p" icon="send" full onClick={() => setMessaging(true)}>
          Message the {n(people.length)} who came
        </Button>
      ) : null}
      {status.kind === "sent" ? (
        <p className="cp-sent">
          <Icon name="check" /> {status.text}
        </p>
      ) : (
        <p className="cp-fnote">{status.text}</p>
      )}
      <div className="cp-nums">
        <div>
          <b>{n(live.cameLive)}</b>
          <span>came</span>
        </div>
        <div>
          <b>{n(live.stayedToEnd)}</b>
          <span>stayed to the end</span>
        </div>
      </div>
      {live.teamTests > 0 ? <p className="cp-fnote">{teamTestsLine(live.teamTests)}</p> : null}
      {messaging ? <MessageSheet detail={detail} people={people} onClose={() => setMessaging(false)} /> : null}
    </section>
  );
}

function LiveCard({ detail, live, here }: { detail: WebinarDetail; live: WebinarLive; here: readonly RoomPerson[] }) {
  const navigate = useNavigate();
  const onAir = live.state === "live";
  return (
    <section className="card cp-live">
      <div className="cp-live-h">
        <Pill tone={onAir ? "livep" : "line"} dot={onAir}>
          {onAir ? "On air" : "Not live yet"}
        </Pill>
        {onAir ? (
          <>
            <span>
              {ranWords(live, Date.now())} · {n(here.length)} here
            </span>
            <FaceStack people={faceList(here)} size={28} />
          </>
        ) : null}
      </div>
      <p>{onAir ? "The recording is on. When you end, the replay and who came show on this page." : "Your link is ready. Open the studio when you want to start."}</p>
      <Button variant="p" size="xl" icon="live" onClick={() => navigate(studioPath(detail.id))}>
        {onAir ? "Back to the studio" : "Open the studio"}
      </Button>
    </section>
  );
}

function Body({ layout, back, children }: { layout: Layout; back: ReactNode; children: ReactNode }) {
  if (layout === "computer") {
    return (
      <div className="page rooms cp-page">
        {back}
        {children}
      </div>
    );
  }
  return (
    <Page layout={layout} narrow className="rm cp-page">
      {back}
      {children}
    </Page>
  );
}

export function QuickLiveView() {
  const { id = "" } = useParams<{ id: string }>();
  const layout = useLayout();
  const navigate = useNavigate();
  const { detail, phase, people, mail, reload } = useQuickLive(id);
  const back =
    layout === "computer" ? (
      <button type="button" className="back" onClick={() => navigate("/rooms")}>
        <Icon name="left" /> Rooms
      </button>
    ) : (
      <PushedNav back={{ to: "/rooms", label: "Rooms" }} />
    );

  if (phase === "error" && !detail) {
    return (
      <Body layout={layout} back={back}>
        {layout === "computer" ? (
          <EmptyState icon="info" title="We couldn't find that call" actions={<Button onClick={() => navigate("/rooms")}>Back to Rooms</Button>} />
        ) : (
          <Empty title="We couldn't find that call">
            <Button onClick={() => navigate("/rooms")}>Back to Rooms</Button>
          </Empty>
        )}
      </Body>
    );
  }
  if (!detail) {
    return (
      <Body layout={layout} back={back}>
        <div className="whead sk-head" aria-busy="true" />
      </Body>
    );
  }
  if (!detail.quick_live) return <Navigate to={`/rooms/${detail.id}`} replace />;
  const live = detail.live;
  if (!live) return <Navigate to="/rooms" replace />;

  const ended = live.state === "ended";
  const came = people ?? [];
  return (
    <Body layout={layout} back={back}>
      <Header detail={detail} live={live} people={came} reload={reload} />
      <div className="cp-cols">
        <div className="cp-main">
          {ended ? <ReplayCard detail={detail} live={live} people={came} /> : <LiveCard detail={detail} live={live} here={came.filter((p) => p.here)} />}
          <WhoCame live={live} people={people} />
        </div>
        {ended ? (
          <div className="cp-rail">
            <FollowUp detail={detail} live={live} people={came} mail={mail} />
          </div>
        ) : null}
      </div>
    </Body>
  );
}
